import type { NeoJob, NeoJobRun, NeoJobWithRuns } from 'src/hooks/useNeoJobs'

/**
 * Cascade 3PL mock agents. Mirrors the AgentRosterCard rendered in chat
 * (`scripts/bulkLabelAgent.ts`) so the sidebar Agents tab and the chat
 * narrative tell the same story.
 *
 * Eight agents across 4 facilities for the $165M Cascade scale. Single
 * source of truth — if the chat script adds a 9th, add it here too.
 */

const NOW = () => new Date()

function isoMinutesAgo(minutes: number): string {
  const date = NOW()
  date.setMinutes(date.getMinutes() - minutes)
  return date.toISOString()
}

function isoHoursFromNow(hours: number): string {
  const date = NOW()
  date.setHours(date.getHours() + hours)
  return date.toISOString()
}

function isoDaysAgo(days: number): string {
  const date = NOW()
  date.setDate(date.getDate() - days)
  return date.toISOString()
}

const COMMON_FIELDS = {
  ownerEmail: 'jordan.castillo@cascade3pl.com',
  ownerAgent: 'NEO' as const,
  mode: 'DETERMINISTIC' as const,
  templateGcsPath: null,
  templateFileName: null,
  templateMimeType: null,
  prompt: null,
  cronTimezone: 'America/Indianapolis',
  runAtUtc: null,
  deliveryMethod: 'SLACK' as const,
  deliveryTarget: '#ops-leads',
  status: 'ACTIVE' as const,
  maxAttempts: 3,
  lookbackMinutes: 60,
  sourceSessionId: null,
  sourceType: 'demo' as const,
  createdAtUtc: isoDaysAgo(45),
  updatedAtUtc: isoDaysAgo(1),
  cancelledAtUtc: null,
  completedAtUtc: null,
  pausedAtUtc: null,
  lastEditedByEmail: null,
  escalatedAt: null,
  archivedAt: null,
}

const TRAILMIX_JOB: NeoJob = {
  ...COMMON_FIELDS,
  id: 'demo-cascade-trailmix-bulkship',
  title: 'Trailmix bulk-ship — Indy 10am',
  toolId: 'shiphero_bulk_ship',
  toolArgs: {
    brand: 'Trailmix Provisions',
    facility: 'Indianapolis HQ',
    packStations: ['Bulk-1', 'Bulk-2', 'Bulk-3', 'Bulk-4', 'Bulk-5', 'Bulk-6'],
    printers: ['Zebra-Bulk-East-1', 'Zebra-Bulk-East-2'],
    floor: 1500,
    ceiling: 4500,
  },
  cronExpression: '0 10 * * 1-5',
  nextRunAtUtc: isoHoursFromNow(22),
  lastRunAtUtc: isoMinutesAgo(180),
  lastRunStatus: 'SUCCEEDED',
}

const BRIAR_JOB: NeoJob = {
  ...COMMON_FIELDS,
  id: 'demo-cascade-briar-bulkship',
  title: 'Briar Coffee bulk-ship — Atlanta 8am',
  toolId: 'shiphero_bulk_ship',
  toolArgs: {
    brand: 'Briar Coffee Roasters',
    facility: 'Atlanta',
    packStations: ['ATL-Bulk-1', 'ATL-Bulk-2', 'ATL-Bulk-3', 'ATL-Bulk-4'],
    printers: ['Zebra-Atlanta-1', 'Zebra-Atlanta-2'],
    floor: 1000,
    ceiling: 3000,
  },
  cronExpression: '0 8 * * 1-5',
  nextRunAtUtc: isoHoursFromNow(20),
  lastRunAtUtc: isoMinutesAgo(300),
  lastRunStatus: 'SUCCEEDED',
}

const NORTHWIND_JOB: NeoJob = {
  ...COMMON_FIELDS,
  id: 'demo-cascade-northwind-bulkship',
  title: 'Northwind bulk-ship — Indy 1pm',
  toolId: 'shiphero_bulk_ship',
  toolArgs: {
    brand: 'Northwind Apparel',
    facility: 'Indianapolis HQ',
    packStations: ['Bulk-1', 'Bulk-2', 'Bulk-3', 'Bulk-4', 'Bulk-5', 'Bulk-6'],
    printers: ['Zebra-Bulk-East-1', 'Zebra-Bulk-East-2'],
    floor: 1500,
    ceiling: 4500,
  },
  cronExpression: '0 13 * * 1-5',
  nextRunAtUtc: isoHoursFromNow(3),
  lastRunAtUtc: isoDaysAgo(1),
  lastRunStatus: 'SUCCEEDED',
}

const OAKHILL_JOB: NeoJob = {
  ...COMMON_FIELDS,
  id: 'demo-cascade-oakhill-bulkship',
  title: 'OakHill bulk-ship — Reno 9am PT',
  toolId: 'shiphero_bulk_ship',
  cronTimezone: 'America/Los_Angeles',
  toolArgs: {
    brand: 'OakHill Skincare',
    facility: 'Reno',
    packStations: ['RNO-Bulk-1', 'RNO-Bulk-2', 'RNO-Bulk-3'],
    printers: ['Zebra-Reno-1'],
    floor: 600,
    ceiling: 2200,
  },
  cronExpression: '0 9 * * 1-5',
  nextRunAtUtc: isoHoursFromNow(21),
  lastRunAtUtc: isoMinutesAgo(420),
  lastRunStatus: 'SUCCEEDED',
}

const ALTITUDE_JOB: NeoJob = {
  ...COMMON_FIELDS,
  id: 'demo-cascade-altitude-bulkship',
  title: 'Altitude bulk-ship — Charlotte 11am',
  toolId: 'shiphero_bulk_ship',
  toolArgs: {
    brand: 'Altitude Supplements',
    facility: 'Charlotte',
    packStations: ['CLT-Bulk-1', 'CLT-Bulk-2', 'CLT-Bulk-3'],
    printers: ['Zebra-Charlotte-1'],
    floor: 800,
    ceiling: 2500,
  },
  cronExpression: '0 11 * * 1-5',
  nextRunAtUtc: isoHoursFromNow(23),
  lastRunAtUtc: isoMinutesAgo(120),
  lastRunStatus: 'SUCCEEDED',
}

const POST_SHIPMENT_JOB: NeoJob = {
  ...COMMON_FIELDS,
  id: 'demo-cascade-post-shipment',
  title: 'Post-shipment monitor',
  mode: 'AGENTIC',
  toolId: null,
  toolArgs: null,
  prompt:
    'Watch every in-transit shipment across all 4 facilities. Surface exceptions (no scan 3+ days, lost, damaged). Draft customer notices, brand-side Slack pings, and replacement orders for one-click approval. Escalate to Jordan when the customer is high-value, dollar amount > $500, or the pattern looks brand-wide.',
  cronExpression: null,
  nextRunAtUtc: isoMinutesAgo(-5),
  lastRunAtUtc: isoMinutesAgo(14),
  lastRunStatus: 'SUCCEEDED',
}

const CLAIMS_JOB: NeoJob = {
  ...COMMON_FIELDS,
  id: 'demo-cascade-carrier-claims',
  title: 'Carrier claims',
  toolId: 'file_carrier_claim',
  toolArgs: { carriers: ['USPS', 'UPS', 'FedEx', 'OnTrac'] },
  cronExpression: '0 7 * * 1-5',
  nextRunAtUtc: isoHoursFromNow(22),
  lastRunAtUtc: isoMinutesAgo(360),
  lastRunStatus: 'SUCCEEDED',
}

const MARGIN_WATCH_JOB: NeoJob = {
  ...COMMON_FIELDS,
  id: 'demo-cascade-margin-watch',
  title: 'Margin trajectory watch',
  mode: 'AGENTIC',
  toolId: null,
  toolArgs: null,
  prompt:
    'Recompute weekly per-customer contribution margin from ShipHero open orders, NetSuite rate card, and pick/pack productivity model. Flag any top-20 brand whose margin has moved more than 500bp over 6 months. Surface on Insights with sized impact and decision options.',
  cronExpression: '0 4 * * *',
  nextRunAtUtc: isoDaysAgo(-1),
  lastRunAtUtc: isoHoursFromNow(-10),
  lastRunStatus: 'SUCCEEDED',
}

const ALL_JOBS: readonly NeoJob[] = [
  TRAILMIX_JOB,
  BRIAR_JOB,
  NORTHWIND_JOB,
  OAKHILL_JOB,
  ALTITUDE_JOB,
  POST_SHIPMENT_JOB,
  CLAIMS_JOB,
  MARGIN_WATCH_JOB,
]

function trailmixRuns(): NeoJobRun[] {
  const baseAttempts = [
    { minutesAgo: 180, status: 'SUCCEEDED' as const, labels: 3200 },
    { minutesAgo: 1620, status: 'SUCCEEDED' as const, labels: 3084 },
    { minutesAgo: 3060, status: 'SUCCEEDED' as const, labels: 3485 },
    { minutesAgo: 4500, status: 'SUCCEEDED' as const, labels: 3210 },
    { minutesAgo: 5940, status: 'SUCCEEDED' as const, labels: 3316 },
    { minutesAgo: 7380, status: 'SUCCEEDED' as const, labels: 2955 },
  ]
  return baseAttempts.map((entry, idx) => ({
    id: `demo-cascade-trailmix-run-${idx}`,
    jobId: TRAILMIX_JOB.id,
    scheduledForUtc: isoMinutesAgo(entry.minutesAgo + 1),
    claimedAtUtc: isoMinutesAgo(entry.minutesAgo + 0.5),
    startedAtUtc: isoMinutesAgo(entry.minutesAgo),
    finishedAtUtc: isoMinutesAgo(entry.minutesAgo - 12),
    status: entry.status,
    attempt: 1,
    error: null,
    outputJson: {
      labelsPrinted: entry.labels,
      packStations: [
        'Bulk-1',
        'Bulk-2',
        'Bulk-3',
        'Bulk-4',
        'Bulk-5',
        'Bulk-6',
      ],
    },
    progressJson: null,
    requestLogId: null,
    archivedAt: null,
  }))
}

function emptyRuns(): NeoJobRun[] {
  return []
}

export function cascadeMockJobs(): NeoJob[] {
  return ALL_JOBS.map((job) => ({ ...job }))
}

export function cascadeMockJobDetail(jobId: string): NeoJobWithRuns | null {
  const match = ALL_JOBS.find((job) => job.id === jobId)
  if (!match) {
    return null
  }
  const runs = match.id === TRAILMIX_JOB.id ? trailmixRuns() : emptyRuns()
  return { ...match, runs }
}
